import {
  useCabinetUsers,
  useMe,
  useUpdateOwnProfile,
  type Fonction,
  type User,
} from '@fidalia/api';
import {
  Alert,
  Button,
  CardDescription,
  CardTitle,
  Field,
  Input,
  PhoneInput,
  Select,
} from '@fidalia/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { MdDeleteOutline, MdMail, MdPerson } from 'react-icons/md';

import { DeleteAccountModal } from '@/features/reglages/delete-account-modal';
import { errorMessage } from '@/lib/auth';
import { SUPPORT_EMAIL } from '@/lib/links';

export const Route = createFileRoute('/_app/settings/account')({
  component: AccountSettings,
});

const FONCTIONS: { value: Fonction; label: string }[] = [
  { value: 'praticien', label: 'Praticien / chirurgien-dentiste' },
  { value: 'assistant', label: 'Assistant(e) dentaire' },
  { value: 'secretariat', label: 'Secrétariat' },
  { value: 'gestionnaire', label: 'Gestionnaire du cabinet' },
];

const PHONE_PATTERN = /^\d[\d\s().-]{5,}$/;

/** Les informations du compte, modifiables, puis la suppression du compte. */
function AccountSettings() {
  const { data: me } = useMe();
  if (!me) return null;

  return (
    <div className="flex flex-col gap-6">
      <div>
        <CardTitle className="flex items-center gap-2">
          <MdPerson aria-hidden="true" className="size-5 text-content-muted" />
          Mon compte
        </CardTitle>
        <CardDescription>Les informations liées à votre accès à Fidalia.</CardDescription>
      </div>
      <hr className="border-border-subtle" />
      {/* Remonté par l'identifiant : un changement de compte repart d'un formulaire neuf. */}
      <ProfileForm key={me.user.id} user={me.user} />
      <DeleteAccountSection user={me.user} statut={me.cabinet.statut} />
    </div>
  );
}

function ProfileForm({ user }: { user: User }) {
  const update = useUpdateOwnProfile();
  const [prenom, setPrenom] = useState(user.prenom);
  const [nom, setNom] = useState(user.nom);
  const [indicatif, setIndicatif] = useState(user.indicatif);
  const [telephone, setTelephone] = useState(user.telephone);
  const [fonction, setFonction] = useState<Fonction>(user.fonction);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const modifications = {
    ...(prenom.trim() !== user.prenom ? { prenom: prenom.trim() } : {}),
    ...(nom.trim() !== user.nom ? { nom: nom.trim() } : {}),
    ...(indicatif !== user.indicatif ? { indicatif } : {}),
    ...(telephone.trim() !== user.telephone ? { telephone: telephone.trim() } : {}),
    ...(fonction !== user.fonction ? { fonction } : {}),
  };
  const modifie = Object.keys(modifications).length > 0;

  const touch = () => {
    setSaved(false);
    setError(null);
  };

  const submit = async () => {
    if (prenom.trim().length < 2) return setError('Indiquez le prénom.');
    if (nom.trim().length < 2) return setError('Indiquez le nom.');
    if (!PHONE_PATTERN.test(telephone.trim())) return setError('Numéro de téléphone invalide.');

    setError(null);
    try {
      await update.mutateAsync(modifications);
      setSaved(true);
    } catch (cause) {
      setError(errorMessage(cause, 'Modification impossible.'));
    }
  };

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        if (modifie) void submit();
      }}
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Prénom" htmlFor="account-prenom">
          <Input
            id="account-prenom"
            autoComplete="given-name"
            value={prenom}
            onChange={(event) => {
              touch();
              setPrenom(event.target.value);
            }}
          />
        </Field>
        <Field label="Nom" htmlFor="account-nom">
          <Input
            id="account-nom"
            autoComplete="family-name"
            value={nom}
            onChange={(event) => {
              touch();
              setNom(event.target.value);
            }}
          />
        </Field>
      </div>

      <Field
        label="Adresse e-mail"
        htmlFor="account-email"
        hint={`Elle sert à vous connecter. Pour la changer, écrivez-nous à ${SUPPORT_EMAIL}.`}
      >
        <Input
          id="account-email"
          type="email"
          value={user.email}
          readOnly
          icon={<MdMail className="size-6" />}
        />
      </Field>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Numéro de téléphone" htmlFor="account-telephone">
          <PhoneInput
            id="account-telephone"
            autoComplete="tel-national"
            dialCode={indicatif}
            onDialCodeChange={(value) => {
              touch();
              setIndicatif(value);
            }}
            value={telephone}
            onChange={(event) => {
              touch();
              setTelephone(event.target.value);
            }}
          />
        </Field>
        <Field label="Fonction" htmlFor="account-fonction">
          <Select
            id="account-fonction"
            value={fonction}
            onChange={(event) => {
              touch();
              setFonction(event.target.value as Fonction);
            }}
          >
            {FONCTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {error ? <Alert tone="danger">{error}</Alert> : null}
      {saved ? <Alert tone="success">Vos informations ont été enregistrées.</Alert> : null}

      <div>
        <Button type="submit" disabled={!modifie} loading={update.isPending}>
          Enregistrer
        </Button>
      </div>
    </form>
  );
}

/**
 * Suppression du compte. Un accès ordinaire ne retire que le sien. Le compte
 * responsable emporte tout le cabinet, ce qui n'est proposé que s'il n'a ni
 * abonnement ni autre utilisateur : sinon la demande passe par le support.
 * Le serveur applique la même règle, cet écran ne fait que l'annoncer.
 */
function DeleteAccountSection({ user, statut }: { user: User; statut: string }) {
  const { data: users } = useCabinetUsers();
  const [deleting, setDeleting] = useState(false);

  const autres = (users?.items ?? []).filter((membre) => membre.id !== user.id).length;
  const abonne = statut === 'actif' || statut === 'impaye';
  const bloque = user.principal && (abonne || autres > 0);
  // Tant que la liste des utilisateurs n'est pas là, on ne propose rien : un
  // bouton qui apparaîtrait puis disparaîtrait serait pire qu'une attente.
  const pret = !user.principal || users !== undefined;

  const titre = (
    <h3 id="danger-zone" className="text-sm font-semibold text-danger">
      Supprimer mon compte
    </h3>
  );

  return (
    <section
      aria-labelledby="danger-zone"
      className="flex flex-wrap items-center justify-between gap-4 rounded-card border border-danger/30 bg-danger-soft/40 p-5"
    >
      {!pret ? (
        titre
      ) : bloque ? (
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {titre}
          <p className="text-sm text-content-muted">
            Vous perdrez l’accès à Fidalia et cette action n’est pas réversible : toutes vos données
            seront supprimées.
            <br />
            Afin de supprimer votre compte, veuillez{' '}
            <a className="font-semibold underline" href={`mailto:${SUPPORT_EMAIL}`}>
              nous contacter
            </a>{' '}
            ({SUPPORT_EMAIL}).
          </p>
        </div>
      ) : (
        <>
          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {titre}
            <p className="text-sm text-content-muted">
              {user.principal
                ? 'Vous perdrez l’accès à Fidalia et cette action n’est pas réversible : toutes vos données seront supprimées.'
                : 'Vous perdrez l’accès à Fidalia avec cette adresse. Le cabinet et ses données ne sont pas supprimés.'}
            </p>
          </div>
          <Button
            variant="danger"
            className="shrink-0"
            icon={<MdDeleteOutline className="size-5" />}
            onClick={() => setDeleting(true)}
          >
            Supprimer mon compte
          </Button>
        </>
      )}

      <DeleteAccountModal
        open={deleting}
        email={user.email}
        cabinet={user.principal}
        onClose={() => setDeleting(false)}
      />
    </section>
  );
}
